import { useEffect, useState } from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { DrawerHeaderWithLeftIcon } from '../../../components/Drawer/DrawerHeader';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import ButtonActions from '../../../components/ButtonActions';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import HorizontalRuleOutlinedIcon from '@mui/icons-material/HorizontalRuleOutlined';
import AdjustOutlinedIcon from '@mui/icons-material/AdjustOutlined';

//header icon
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { Timeline, TimelineIcon } from '../../../components/Timeline';
import { useGetCarcinogenicSrtHistorialQuery } from '../../../services/carcinogenicApi';
import { construirEstadosPorPaso } from './srtHistorial';
import { CARCINOGENIC_SRT_STATES, PresentationSRTStateChip } from './PresentationStateChip';
import { getPresentationSVCCStateSRT } from '..';

const srtSteps = {
    historial: [
        {
            title: 'Validación de credenciales SRT e Inicio de presentación',
            icon: 'circle-check' as const,
            isLastStep: false,
            currentStep: 1
        },
        {
            title: 'Carga de establecimiento',
            icon: 'circle-check' as const,
            isLastStep: false,
            currentStep: 2
        },
        {
            title: 'Carga de sustancias',
            icon: 'circle-check' as const,
            isLastStep: false,
            currentStep: 3
        },
        {
            title: 'Carga de nómina',
            icon: 'circle-check' as const,
            isLastStep: false,
            currentStep: 4
        },
        {
            title: 'Consolidación y acuse',
            icon: 'circle-check' as const,
            isLastStep: true,
            currentStep: 5
        }
    ]
};

interface PresentationProcessItemProps {
    title: string;
    estado: 'done' | 'in-progress' | 'pending' | 'error';
    isLastStep: boolean;
    errorMessage?: string;
    currentStep?: number;
}

const getIconPropertiesByHistoryItem = (
    stepState: 'done' | 'in-progress' | 'pending' | 'error'
) => {
    const baseIconStyles = {
        iconWidth: 25,
        marginLeft: 1,
        fontSize: 15
    };

    switch (stepState) {
        case 'done':
            return {
                ...baseIconStyles,
                borderColor: '#22C55E',
                backgroundColor: '#ECFDF5',
                icon: (
                    <CheckOutlinedIcon
                        color="success"
                        fontSize="small"
                        sx={{ fontSize: baseIconStyles.fontSize }}
                    />
                )
            };
        case 'in-progress':
            return {
                ...baseIconStyles,
                borderColor: '#EAB308',
                backgroundColor: '#FFFBEB',
                icon: (
                    <InfoOutlinedIcon
                        sx={{ color: '#EAB308', fontSize: baseIconStyles.fontSize }}
                        fontSize="small"
                    />
                )
            };
        case 'pending':
            return {
                ...baseIconStyles,
                borderColor: '#94a3b8',
                backgroundColor: '#FFFBEB',
                icon: (
                    <AdjustOutlinedIcon
                        sx={{ color: '94a3b8', fontSize: baseIconStyles.fontSize }}
                        fontSize="small"
                    />
                )
            };
        case 'error':
            return {
                ...baseIconStyles,
                borderColor: '#DC2626',
                backgroundColor: '#FEE2E2',
                icon: (
                    <InfoOutlinedIcon
                        sx={{ color: '#DC2626', fontSize: baseIconStyles.fontSize }}
                        fontSize="small"
                    />
                )
            };
        default:
            return {
                ...baseIconStyles,
                borderColor: '#E2E8F0',
                backgroundColor: '#F8FAFC',
                icon: (
                    <HorizontalRuleOutlinedIcon
                        sx={{ color: '#E2E8F0', fontSize: baseIconStyles.fontSize }}
                        fontSize="small"
                    />
                )
            };
    }
};

interface PresentationProcessItemProps {
    title: string;
    estado: 'done' | 'in-progress' | 'pending' | 'error';
    isLastStep: boolean;
    errorMessage?: string;
    errorCode?: string;
    currentStep?: number;
    displayError: boolean;
    translate: (key: string) => string;
}

const getStateBadgeColor = (srtState: 'done' | 'in-progress' | 'pending' | 'error') => {
    switch (srtState) {
        case 'done':
            return {
                color: '#16A34A',
                borderColor: '#22C55E',
                backgroundColor: '#ECFDF5'
            };
        case 'in-progress':
            return {
                color: '#CA8A04',
                borderColor: '#EAB308',
                backgroundColor: '#FFFBEB'
            };
        case 'pending':
            return {
                color: '#64748B',
                borderColor: '#94a3b8',
                backgroundColor: '#FFFBEB'
            };
        case 'error':
            return {
                color: '#991B1B',
                borderColor: '#DC2626',
                backgroundColor: '#FEE2E2'
            };
        default:
            return {
                borderColor: '#E2E8F0',
                backgroundColor: '#F8FAFC'
            };
    }
};

const PresentationProcessItem = ({
    title,
    estado,
    errorMessage,
    errorCode,
    isLastStep,
    displayError,
    translate,
    fecha
}: PresentationProcessItemProps & { fecha?: string }) => {

    return (
        <Stack spacing={1} flex={1} pb={isLastStep ? 0 : 2} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} pl={2}>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        {title}
                    </Typography>
                    {fecha && (
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {new Date(fecha).toLocaleString('es-AR')}
                        </Typography>
                    )}
                </Box>
                <Chip
                    label={translate(`drawers.srtInformation.srtStates.${estado}`)}
                    size="small"
                    sx={getStateBadgeColor(estado)}
                />
            </Box>
            {displayError && errorMessage && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                        marginTop: 1,
                        padding: 1,
                        borderRadius: 1,
                        backgroundColor: '#FEE2E2',
                        border: '1px solid #FCA5A5'
                    }}
                >
                    <InfoOutlinedIcon sx={{ color: '#DC2626', fontSize: 16 }} />
                    <Typography variant="caption" sx={{ color: '#DC2626' }}>
                        {`${errorMessage || 'Error desconocido'} ${errorCode ? `(Código de error: ${errorCode})` : ''}`}
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

const SRTInformationDrawer = ({
    handleCloseDrawer,
    translate,
    selected,
    handleSendCarcinogenicReportToSRT,
    isSendingCarcinogenicReportToSRT,
}: any) => {
    const srtState = getPresentationSVCCStateSRT(selected?.cancerigenosSrt);
    const [allowFetchWhenPending, setAllowFetchWhenPending] = useState(false);

    useEffect(() => {
        setAllowFetchWhenPending(false);
    }, [selected?.id]);

    const shouldSkipSrtHistoryQuery =
        !selected?.id ||
        (srtState === CARCINOGENIC_SRT_STATES.PENDING && !allowFetchWhenPending);

    const { data: srtHistorial, isFetching: isFetchingSrtHistory } =
        useGetCarcinogenicSrtHistorialQuery(selected?.id, {
            skip: shouldSkipSrtHistoryQuery
        });

    const estadosPorPaso = construirEstadosPorPaso(srtHistorial?.data);

    useEffect(() => {
        if (!shouldSkipSrtHistoryQuery && !allowFetchWhenPending) {
            setAllowFetchWhenPending(true);
        }
    }, [shouldSkipSrtHistoryQuery, allowFetchWhenPending]);

    const handleSendPresentationToSRTClick = async () => {
        setAllowFetchWhenPending(true);
        await handleSendCarcinogenicReportToSRT(selected?.id);
        handleCloseDrawer();
    };

    return (
        <Box
            display="flex"
            flexDirection={'column'}
            justifyContent="space-between"
            sx={{ flexDirection: 'column', height: '100%', minHeight: '100%' }}
        >
            <Grid p={1}>
                <DrawerHeaderWithLeftIcon
                    onIconClick={handleCloseDrawer}
                    text={translate('drawers.srtInformation.title')}
                    subtitle={`${selected?.establecimiento?.nombre}`}
                    LeftIcon={<DescriptionOutlinedIcon sx={{ fontSize: 20, color: '#15803d' }} />}
                />
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: '#e2e8f0',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                        Estado SRT actual
                    </Typography>
                    <PresentationSRTStateChip
                        presentationState={getPresentationSVCCStateSRT(selected?.cancerigenosSrt)}
                    />
                </Box>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: '#e2e8f0',
                        bgcolor: '#ffffff',
                        mt: 2
                    }}
                >
                    <Typography variant="subtitle1" sx={{ color: '#475569', fontSize: 16, mb: 2 }}>
                        Línea de tiempo (5 pasos)
                    </Typography>
                    <Timeline>
                        {srtSteps?.historial?.map((processItem: any, index: number) => {
                            const estadoPaso = estadosPorPaso[index];
                            return (
                                <Box display="flex" key={index}>
                                    <TimelineIcon
                                        {...getIconPropertiesByHistoryItem(estadoPaso.estado)}
                                    />
                                    <PresentationProcessItem
                                        title={processItem.title}
                                        translate={translate}
                                        estado={estadoPaso.estado}
                                        fecha={estadoPaso.fecha}
                                        errorMessage={estadoPaso.errorMessage}
                                        errorCode={estadoPaso.errorCode}
                                        displayError={estadoPaso.estado === 'error'}
                                        isLastStep={processItem.isLastStep}
                                    />
                                </Box>
                            );
                        })}
                    </Timeline>
                </Box>
            </Grid>

            <Grid container direction="row" alignItems="center" sx={{ marginTop: '1rem' }}>
                <Box
                    sx={{
                        '& > :not(style)': { m: 1 },
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}
                >
                    {/* <Typography variant="subtitle2">{`Intentos realizados: ${0}`}</Typography> */}
                    <></>

                    {/* SRT STATE PENDING */}
                    {getPresentationSVCCStateSRT(selected?.cancerigenosSrt) === CARCINOGENIC_SRT_STATES.PENDING && (
                        <ButtonActions
                            handleClose={handleCloseDrawer}
                            renderBackAction
                            returnText={translate('drawers.srtInformation.buttons.cancel')}
                            confirmText={translate('drawers.srtInformation.buttons.present')}
                            loading={false}
                            disabled={false}
                            onClick={handleSendPresentationToSRTClick}
                            borderRadius="4px"
                            size="small"
                        />
                    )}

                    {/* SRT STATE ERROR */}
                    {getPresentationSVCCStateSRT(selected?.cancerigenosSrt) === CARCINOGENIC_SRT_STATES.ERROR && (
                        <ButtonActions
                            handleClose={handleCloseDrawer}
                            renderBackAction
                            returnText={translate('drawers.srtInformation.buttons.cancel')}
                            confirmText={translate('drawers.srtInformation.buttons.retry')}
                            loading={isSendingCarcinogenicReportToSRT}
                            disabled={isSendingCarcinogenicReportToSRT}
                            onClick={handleSendPresentationToSRTClick}
                            borderRadius="4px"
                            size="small"
                        />
                    )}

                    {/* SRT STATE COMPLETED */}
                    {getPresentationSVCCStateSRT(selected?.cancerigenosSrt) === CARCINOGENIC_SRT_STATES.PRESENTED && (
                        <Button variant="outlined" onClick={handleCloseDrawer}>
                            Cerrar
                        </Button>
                    )}
                </Box>
            </Grid>
            <BackdropComponent loading={isSendingCarcinogenicReportToSRT || isFetchingSrtHistory} />
        </Box>
    );
};

export { SRTInformationDrawer };
